import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
    catchError,
    Observable,
    of,
    ReplaySubject,
    switchMap,
    throwError,
    EMPTY
} from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { GeneralResponse } from 'app/models/general-response.model';
import { FiltersService } from 'app/layout/common/filters/filters.service';

@Injectable()
export class AuthService {
    private _authenticated = false;
    private localRoles: string[];
    private _userRoles = new ReplaySubject<string[]>(1);

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _filtersService: FiltersService
    ) {}

    // --------------------------------------------------------
    // Accessors
    // --------------------------------------------------------
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }
    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    get userRoles(): Observable<string[]> {
        return this._userRoles.asObservable();
    }

    set fishTrackUserName(userName: string) {
        localStorage.setItem('fishTrackUserName', userName);
    }
    get fishTrackUserName(): string {
        return localStorage.getItem('fishTrackUserName') ?? '';
    }

    // --------------------------------------------------------
    // Core methods
    // --------------------------------------------------------

    /**
     * Sign in
     */
    signIn(credentials: { username: string; password: string }): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient
            .post(`${environment.backendURL}${environment.loginURL}`, credentials)
            .pipe(
                switchMap((response: any) => {
                    console.log('[AuthService] Login response:', response);

                    //  Handle nested response structure
                    const nestedWorkload = response?.workload?.workload || response?.workload || {};

                    // Extract access token (supports token or accessToken)
                    const token = nestedWorkload?.accessToken || nestedWorkload?.token || null;

                    if (!token) {
                        console.error('[AuthService] No access token found in response:', response);
                        throw new Error('Missing access token in response.');
                    }

                    // Normal login process
                    this.accessToken = token;
                    this.fishTrackUserName = nestedWorkload?.username || '';
                    this._authenticated = true;
                    this._userService.user = nestedWorkload;

                    const roles = nestedWorkload?.roles || [];
                    this._userRoles.next(roles);
                    this.localRoles = roles;

                    console.log('[AuthService] Login success for:', this.fishTrackUserName);
                    return of(response);
                }),
                catchError((error: HttpErrorResponse) => {
                    //  Handle special backend responses (like password expiry)
                    if (error.status === 403) {
                        console.warn('[AuthService] Password change required before login.');
                        return of({
                            forcePasswordChange: true,
                            msg: error.error?.workload?.msg || 'Password change required before login.'
                        });
                    }

                    console.error('[AuthService] Login failed:', error);
                    return throwError(() => new Error(error.message || 'Login failed.'));
                })
            );
    }



    /**
     * Change password
     */
    changePassword(payload: { username: string; oldPassword: string; newPassword: string }): Observable<any> {
        return this._httpClient
            .post(`${environment.backendURL}/auth/change-password`, payload)
            .pipe(
                catchError((error) => {
                    console.error('[AuthService] Password change failed:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Sign in using token
     */
    signInUsingToken(): Observable<any> {
        const token = this.accessToken;

        // Skip if no token stored
        if (!token) {
            console.warn('[AuthService] No access token found for refresh.');
            return of(false);
        }

        return this._httpClient
            .get<any>(`${environment.backendURL}/auth/refresh-access-token`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    console.error('[AuthService] Token refresh failed:', error);
                    this.signOut();
                    return of(false);
                }),
                switchMap((response: any) => {
                    console.log('[AuthService] Refresh response:', response);

                    //  Handle nested structure
                    const nestedWorkload = response?.workload?.workload || response?.workload || {};

                    const token = nestedWorkload?.accessToken || nestedWorkload?.token || null;

                    if (token) {
                        this.accessToken = token;
                        this.fishTrackUserName = nestedWorkload?.username || '';
                        this._authenticated = true;
                        this._userService.user = nestedWorkload;

                        const roles = nestedWorkload?.roles || [];
                        this._userRoles.next(roles);
                        this.localRoles = roles;

                        console.log('[AuthService] Token refreshed successfully for:', this.fishTrackUserName);
                        return of(true);
                    }

                    console.warn('[AuthService] No token found in refresh response.');
                    return of(false);
                })

            );
    }



    /**
     * Sign out
     */
    signOut(): Observable<any> {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('fishTrackUserName');
        this._authenticated = false;
        return of(true);
    }

    /**
     * Check authentication
     */
    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        if (!this.accessToken || this.accessToken === 'undefined') {
            return of(false);
        }

        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        return this.signInUsingToken();
    }

    hasrole(route): Observable<boolean> | boolean {
        const expectedRoles = route.data.expectedRoles;
        if (this.localRoles?.length) {
            return this.localRoles.some((role) => expectedRoles.includes(role));
        }
        return false;
    }
}
